import {
    Box,
    IconButton,
    Toolbar,
    Grid2,
    Grid,
    Tooltip,
    useMediaQuery,
    Drawer as MuiDrawer,
} from '@mui/material';
import { Menu, MenuOpen } from '@mui/icons-material';
import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Drawer, DrawerHeader, AppBar, DRAWER_WIDTH } from './Drawer';
import SidebarMenu from './SidebarMenu';
import { useAuth } from '../../hooks/useAuth';
import Profile from './Profile';
import BackIcon from '@mui/icons-material/ArrowBackIosNew';
import { ROUTES } from '../../App';
import { useTranslation } from 'react-i18next';
import TopLogo from './TopLogo';

const menuOpenStorageKey = (userId) => `menu_open_${userId}`;

const HeaderInner = ({ user }) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    // Float the drawer as an overlay on any touch device (phone/tablet) regardless
    // of width - so landscape tablets a width check would miss get it too; desktops
    // (fine pointer) keep the permanent drawer.
    const isMobile = useMediaQuery('(pointer: coarse)');
    const userId = user?.id;
    const [open, setOpen] = useState(() => {
        if (!userId) return false;
        const saved = localStorage.getItem(menuOpenStorageKey(userId));
        return saved === null ? true : saved === 'true';
    });
    // Mobile uses a temporary overlay drawer with its own state, so it never
    // auto-opens from the persisted desktop preference nor shifts the AppBar.
    const [mobileOpen, setMobileOpen] = useState(false);
    const drawerOpen = isMobile ? mobileOpen : open;

    const handleDrawerOpen = () => {
        if (isMobile) {
            setMobileOpen(true);
            return;
        }
        setOpen(true);
        if (userId) localStorage.setItem(menuOpenStorageKey(userId), 'true');
    };
    const handleDrawerClose = () => {
        if (isMobile) {
            setMobileOpen(false);
            return;
        }
        setOpen(false);
        if (userId) localStorage.setItem(menuOpenStorageKey(userId), 'false');
    };

    const backUrl = useMemo(() => {
        // Match pathname against ROUTES, capturing :params so backUrl can reference them.
        for (const route of ROUTES) {
            const paramNames = [];
            const pathPattern = route.path.replace(/:(\w+)/g, (_, name) => {
                paramNames.push(name);
                return '([^/]+)';
            });
            const match = new RegExp(`^${pathPattern}$`).exec(pathname);
            if (!match) continue;
            if (!route.backUrl) return false;
            if (typeof route.backUrl !== 'string') return route.backUrl;
            return route.backUrl.replace(/:(\w+)/g, (token, name) => {
                const idx = paramNames.indexOf(name);
                return idx >= 0 ? match[idx + 1] : token;
            });
        }
        return false;
    }, [pathname]);

    const drawerHeader = (
        <DrawerHeader>
            <IconButton onClick={handleDrawerClose}>
                <MenuOpen />
            </IconButton>
        </DrawerHeader>
    );

    return (
        <>
            <AppBar position="fixed" open={!isMobile && open}>
                <Toolbar sx={{ position: 'relative' }}>
                    {/* Absolutely-centered logo: keeps it on true center of the bar
                        regardless of the (asymmetric) menu/back vs profile side content. */}
                    {!(isMobile && mobileOpen) && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 'min(300px, 60vw)',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Link to="/venues">
                                <TopLogo text={t('brand')} />
                            </Link>
                        </Box>
                    )}
                    {user ? (
                        <IconButton
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}
                            edge="start"
                            sx={{
                                // No gap when the drawer floats (touch); roomy on desktop.
                                mr: isMobile ? 0 : 5,
                            }}
                            style={{
                                ...(drawerOpen && { display: 'none' }),
                            }}
                        >
                            <Menu />
                        </IconButton>
                    ) : null}
                    <Grid
                        container
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ flexGrow: 1 }}
                    >
                        <Grid2>
                            {user && backUrl && backUrl !== false && (
                                <Tooltip title={t('common.back')}>
                                    <IconButton
                                        aria-label="back"
                                        edge="start"
                                        sx={{ mr: 2 }}
                                        onClick={() => {
                                            if (window.history.state?.idx > 0) {
                                                navigate(-1);
                                            } else {
                                                navigate(backUrl);
                                            }
                                        }}
                                    >
                                        <BackIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Grid2>
                        {user && (
                            <Grid2>
                                <Profile />
                            </Grid2>
                        )}
                    </Grid>
                </Toolbar>
            </AppBar>
            {user ? (
                isMobile ? (
                    <MuiDrawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={handleDrawerClose}
                        slotProps={{ paper: { sx: { width: DRAWER_WIDTH } } }}
                    >
                        {drawerHeader}
                        <SidebarMenu open onNavigate={handleDrawerClose} />
                    </MuiDrawer>
                ) : (
                    <Drawer variant="permanent" open={open}>
                        {drawerHeader}
                        <SidebarMenu open={open} />
                    </Drawer>
                )
            ) : null}
        </>
    );
};

const Header = () => {
    const { user } = useAuth();
    return <HeaderInner key={user?.id ?? 'anon'} user={user} />;
};

export default Header;
